import { GeographicRegion } from '../types/GeographicRegion';
import { Country } from '../types/Country';
import { Office } from '../../contacts/types/Office';
import { Distributor } from '../../contacts/types/Distributor';
import { Representative } from '../../contacts/types/Representative';

/**
 * Generic filter: retains geographic regions whose countries or subnational regions
 * satisfy provided item-to-id mappings.
 *
 * @param items Array of arbitrary items to derive country/subnational IDs from
 * @param geographicRegions Regions to be filtered
 * @param getCountryIds Callback to extract country IDs from an item
 * @param getSubnationalIds Optional callback to extract subnational region IDs from an item
 * @returns Filtered array of GeographicRegion
 */
export function filterGeographicRegionsByItems<T>(
    items: T[],
    geographicRegions: GeographicRegion[],
    getCountryIds: (item: T) => string[],
    getSubnationalIds?: (item: T) => string[]
): GeographicRegion[] {
    const countryIdSet = new Set<string>();
    const subnationalIdSet = new Set<string>();

    for (const item of items) {
        getCountryIds(item).forEach((id) => countryIdSet.add(id));
        if (getSubnationalIds) {
            getSubnationalIds(item).forEach((id) => subnationalIdSet.add(id));
        }
    }

    return geographicRegions
        .map((region) => {
            const filteredCountries = region.countries
                .map((country) => {
                    const filteredSubnationals = getSubnationalIds
                        ? country.subnationalRegions.filter((sub) => subnationalIdSet.has(sub.id))
                        : [];

                    const includeCountry = countryIdSet.has(country.id);
                    if (includeCountry || filteredSubnationals.length > 0) {
                        return {
                            ...country,
                            subnationalRegions: filteredSubnationals,
                        };
                    }
                    return null;
                })
                .filter((c): c is Country => c !== null);

            return {
                ...region,
                countries: filteredCountries,
            };
        })
        .filter((region) => region.countries.length > 0);
}

// Specific-case wrappers

/** Filter regions by Office.countryId */
export const filterGeographicRegionsByOffices = (
    offices: Office[],
    geographicRegions: GeographicRegion[]
): GeographicRegion[] =>
    filterGeographicRegionsByItems(offices, geographicRegions, (office) => [office.countryId]);

/** Filter regions by Distributor.countryIds */
export const filterGeographicRegionsByDistributors = (
    distributors: Distributor[],
    geographicRegions: GeographicRegion[]
): GeographicRegion[] =>
    filterGeographicRegionsByItems(distributors, geographicRegions, (distro) => distro.countryIds);

/** Filter regions by Representative.countryOrSubnational (for both countries and subnationals) */
export const filterGeographicRegionsByRepresentatives = (
    representatives: Representative[],
    geographicRegions: GeographicRegion[]
): GeographicRegion[] =>
    filterGeographicRegionsByItems(
        representatives,
        geographicRegions,
        (rep) => rep.countryOrSubnational,
        (rep) => rep.countryOrSubnational
    );

/**
 * Filter list of countries by representatives' regions or subnationals.
 */
export const filterCountriesByRepresentatives = (
    representatives: Representative[],
    geographicRegions: GeographicRegion[]
): Country[] =>
    filterGeographicRegionsByRepresentatives(representatives, geographicRegions).flatMap(
        (region) => region.countries
    );
